// Login page

import {
  Box,
  Container,
  Heading,
  VStack,
  Image,
  Text,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  Spinner,
} from '@chakra-ui/react';
import { MdCheckCircle, MdWhatsapp } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '../components/LoginForm';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { useEffect } from 'react';
import LogoSvg from '@/assets/logo.svg';

/**
 * Login page with WhatsApp authentication
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyOTP, isAuthenticated, isLoading } = useAuth();
  const toast = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/client/orgs');
    }
  }, [isAuthenticated, navigate]);

  // Show loading spinner while auth state initializes
  if (isLoading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="bg-canvas">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Box>
    );
  }

  // Redirect authenticated users - don't show login form
  if (isAuthenticated) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" bg="bg-canvas">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Box>
    );
  }

  const handleLogin = async (data: { phone_number: string; country_code: string }) => {
    try {
      await login(data.phone_number, data.country_code);
      toast.success('OTP sent successfully to your WhatsApp');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send OTP');
      throw error;
    }
  };

  const handleVerifyOTP = async (
    mobile: string,
    countryCode: string,
    otp: number
  ) => {
    try {
      await verifyOTP(mobile, countryCode, otp);
      toast.success('Login successful');
      navigate('/client/orgs');
    } catch (error: any) {
      toast.error(error.message || 'Invalid OTP');
      throw error;
    }
  };

  return (
    <Box minH="100vh" bg="bg-canvas" display="flex" alignItems="center" py={12}>
      <Container maxW="md">
        <VStack spacing={8}>
          {/* Logo and Branding */}
          <VStack spacing={4}>
            <Image src={LogoSvg} alt="TaskEye Logo" boxSize="80px" />
            <VStack spacing={2}>
              <Heading size="2xl" color="brand.500">
                TaskEye
              </Heading>
              <Heading size="md" fontWeight="normal" color="text-secondary">
                Client Portal
              </Heading>
            </VStack>
          </VStack>

          {/* WhatsApp Verification Alert */}
          <Alert
            status="info"
            variant="subtle"
            borderRadius="lg"
            flexDirection="column"
            alignItems="start"
            textAlign="left"
            py={4}
          >
            <HStack spacing={2} mb={2}>
              <AlertIcon as={MdWhatsapp} boxSize={6} />
              <AlertTitle fontSize="lg">WhatsApp Verification Required</AlertTitle>
            </HStack>
            <AlertDescription fontSize="sm" color="text-secondary">
              <List spacing={2} mt={2}>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={MdCheckCircle} color="status-success" mt={0.5} />
                  <Text>
                    Your phone number must be verified with TaskEye via WhatsApp before you can
                    access the client portal
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={MdCheckCircle} color="status-success" mt={0.5} />
                  <Text>
                    If you haven't received verification, please contact your organization
                    administrator
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="start">
                  <ListIcon as={MdCheckCircle} color="status-success" mt={0.5} />
                  <Text>
                    Use the same phone number that's registered with your WhatsApp account
                  </Text>
                </ListItem>
              </List>
            </AlertDescription>
          </Alert>

          {/* Login Form */}
          <Box
            bg="bg-surface"
            p={8}
            borderRadius="lg"
            borderWidth="1px"
            borderColor="border-default"
            w="full"
            shadow="sm"
          >
            <LoginForm onLogin={handleLogin} onVerifyOTP={handleVerifyOTP} />
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
